<template>
  <v-app id="app">
    <!-- App Bar -->
    <v-app-bar app clipped-left clipped-right color="primary">
      <div class="app-bar-grid fill-height">
        <!-- Logo -->
         <div class="d-flex flex align-center flex-grow-0 fill-height">
          <v-btn plain variant="text" width="70" :to="{ name: 'Home' }" :ripple="false" class="no-hover">
            <v-img contain width="70" :src="logo" alt="ROM"/>
          </v-btn>
          <h2 class="ml-2 text-white flex-grow-1">EARNS Web</h2>
         </div>
        
        


        <!-- Project Title (center column; cell always rendered so the grid stays 3-up) -->
        <div class="app-bar-title d-flex align-center justify-center fill-height">
          <h1 v-if="title" class="text-white">{{ title }}</h1>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex flex justify-end mr-2 align-center flex-grow-0 fill-height">

          <!-- Theme logic tip -->
          <v-tooltip
            location="bottom"
            color="surface"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                variant="text"
                class="mr-2 theme-mode-button"
                :aria-label="themeLabel"
                @click="store.cycleThemeMode()"
              >
                <v-icon
                  :icon="themeIcon"
                  color="white"
                />
              </v-btn>
            </template>

            <span class="theme-tooltip-text">
              {{ themeLabel }}
            </span>
          </v-tooltip>

          <!-- Logged-out actions -->
          <v-btn
            v-if="showLogin"
            class="mr-3"
            :to="{ name: 'Login' }"
            @click="print"
            variant="text"
          >
            Login
          </v-btn>
          <v-btn
            v-if="showRegister"
            :to="{ name: 'Register' }"
            @click="print"
            variant="text"
          >
            Registrieren
          </v-btn>

          <!-- Account menu (logged in) -->
          <v-menu
            v-if="showLogout"
            location="bottom end"
            min-width="220"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                variant="text"
                aria-label="Konto"
              >
                <v-icon icon="mdi-account-circle" color="white" />
              </v-btn>
            </template>

            <v-list density="compact" nav>
              <v-list-item
                v-if="store.user?.email"
                title="Angemeldet als"
                :subtitle="store.user.email"
              />
              <v-divider v-if="store.user?.email" />

              <v-list-item
                :to="{ name: 'ChangePassword' }"
                prepend-icon="mdi-lock-reset"
                title="Passwort ändern"
              />
              <v-list-item
                v-if="showAdmin"
                :href="adminUrl"
                target="_blank"
                prepend-icon="mdi-shield-account-outline"
                title="Admin"
              />

              <v-divider />

              <v-list-item
                prepend-icon="mdi-logout"
                title="Ausloggen"
                @click="logout"
              />
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-app-bar>

    <!-- Main Content -->

<v-main>
  <v-container fluid class="pt-6 px-4">
    <!-- Prevent wrapping so columns stay side-by-side -->
    <v-row class="row-no-wrap overflow-x">
      <!-- Slim strip column (fixed px width; adjust 72 -> the circle + gap) -->
      <v-col
        v-if="store.isLoggedIn && !onForgotPasswordPage && !onNotFoundPage"
        class="menu-strip"
      >
        <!-- PLACE THE MENU HERE (only here; remove any other navbar inside v-main) -->
        <NavBar />
      </v-col>

      <!-- Content column (fills the rest) -->
      <v-col class="content-col mx-auto" >
        <div class="page-gutters">
          <!-- Keep your alert here -->
          <v-row justify="center" class="mt-0" v-if="!title">
            <v-alert
              v-model="cookiesDisabled"
              border="start"
              variant="text"
              elevation="3"
              dismissible
              type="error"
              style="width: 90%"
            >
              We're sorry but Earns doesn't work properly without Cookies enabled.
              Please enable it to continue.
            </v-alert>
          </v-row>

          <!-- Your page content -->
          <router-view :key="route.fullPath" />
        </div>
      </v-col>
    </v-row>
  </v-container>
</v-main>

    <!-- Footer -->
    <v-footer app color="surface" class="pa-3 app-footer">
      
      <v-btn 
        :to="{ name: 'Imprint' }" 
        variant="text"
      >
        Impressum
      </v-btn>

      <v-btn
        v-if="showContact"
        :to="{ name: 'Contact' }"
        variant="text"
      >
        Kontakt
      </v-btn>

      <v-btn 
        href="mailto:earns@rom-technik.de?subject=Support"
        variant="text"
      >
        Support
      </v-btn>

    </v-footer>

    <!-- Einmal für die ganze Anwendung; die Seiten öffnen ihn über den
         Modulzustand in `useUnsavedChanges`. -->
    <UnsavedChangesDialog />
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTheme } from 'vuetify'
import { useProjectStore } from '@/stores/projectStore'
import NavBar from '@/components/NavBar.vue'
import UnsavedChangesDialog from '@/components/UnsavedChangesDialog.vue'
import logo from '@/assets/logo.png'

const store = useProjectStore()
const route = useRoute()
const router = useRouter()

const cookiesDisabled = ref(false)

const title = computed(() => store.title)
const onForgotPasswordPage = computed(() => route.name === 'ForgotPassword')

const onNotFoundPage = computed(() => route.name === 'NotFound')


const onLoginPage = computed(() => route.name === 'Login')
const onRegisterPage = computed(() => route.name === 'Register')

const showLogin = computed(() => 
  !store.isLoggedIn && !onLoginPage.value && !onForgotPasswordPage.value
)

const showRegister = computed(() => 
  !store.isLoggedIn && !onRegisterPage.value && !onForgotPasswordPage.value
)

const showLogout = computed(() => 
  store.isLoggedIn && !onForgotPasswordPage.value
)

const showAdmin = computed(() => 
  store.isLoggedIn && store.isAdmin && !onForgotPasswordPage.value
)

const showContact = computed(() => 
  store.isLoggedIn && !onForgotPasswordPage.value
)

const adminUrl = computed(() => {
  const configured = import.meta.env.VITE_ADMIN_URL as string | undefined
  if (configured && configured.trim()) {
    return configured
  }

  if (import.meta.env.DEV) {
    return `${window.location.protocol}//${window.location.hostname}:8000/admin/`
  }

  return `${window.location.origin}/admin/`
})

function logout() {
  store.logout()
  router.replace({ name: 'Login' });
}

function print() {
  console.log(store.forgotPassword)
}


onMounted(() => {
  store.loadThemeMode()
  applyThemeMode()

  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', handleSystemThemeChange)

  if (!navigator.cookieEnabled) {
    cookiesDisabled.value = true
  }
})

onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', handleSystemThemeChange)
})


// --------------------------
// Themes
// --------------------------
const theme = useTheme() 


let mediaQuery: MediaQueryList | null = null

function getSystemTheme() {
  if (typeof window === 'undefined') {
    return 'light'
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function applyThemeMode() {
  const resolvedTheme =
    store.themeMode === 'system'
      ? getSystemTheme()
      : store.themeMode

  theme.change(resolvedTheme)
}

function handleSystemThemeChange() {
  if (store.themeMode === 'system') {
    applyThemeMode()
  }
}

const themeIcon = computed(() => {
  if (store.themeMode === 'system') return 'mdi-monitor'
  if (store.themeMode === 'dark') return 'mdi-weather-night'
  return 'mdi-weather-sunny'
})

const themeLabel = computed(() => {
  if (store.themeMode === 'system') return 'Systemmodus'
  if (store.themeMode === 'dark') return 'Dunkler Modus'
  return 'Heller Modus'
})

watch(
  () => store.themeMode,
  () => {
    applyThemeMode()
  },
  { immediate: true }
)

</script>

<style scoped lang="scss">
.v-footer {
  justify-content: center;
}

.v-toolbar__content {
  justify-content: space-between;
}



.bar-title {
  color: #fff;
}

.no-hover {
  padding-right: 4px;
  background-color: transparent !important;
  box-shadow: none !important;
  opacity: 1 !important;
}

.no-hover:hover {
  background-color: transparent !important;
  opacity: 1 !important;
}

:deep(.no-hover .v-btn__overlay) {
  display: none !important;
}


/* App bar uses a 3-column grid so the project title stays truly centered
   between the logo and action groups (which share equal side space),
   instead of relying on fixed/absolute positioning. */
.app-bar-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;

  width: 100%;
  height: 100%;
}

.app-bar-title {
  min-width: 0;
}

.app-bar-title h1 {
  margin: 0;
  white-space: nowrap;
}


/* Do not allow the grid to wrap; columns stay side-by-side */
.row-no-wrap {
  flex-wrap: nowrap !important;
}


/* Add horizontal scroll when the nowrap content exceeds viewport (zoom-in safe) */
// .overflow-x {
//   overflow-x: auto;   /* enables scroll instead of clipping */
//   overflow-y: hidden;
// }


/* Let the slim column allow hover expansion to overlap the content */
.menu-strip {
  position: relative;
  overflow: visible; /* critical: allows the button pill to overlap the right column on hover */
  
  flex: 0 0 72px;
  width: 72px;
  min-width: 72px;
  max-width: 72px;

}

/* Content flex item is allowed to shrink (prevents layout overflow) */
.content-col {
  min-width: 0; /* important for flexbox to allow shrinking */
  flex: 1 1 auto;
  max-width: 100%;
}

.theme-mode-button {
  color: #fff;
}

.theme-mode-button:hover {
  background-color: rgba(255, 255, 255, 0.12);
}

.theme-tooltip-text {
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
}


/*
 * Tooltip content is teleported outside this component.
 * :global is therefore required inside scoped styles.
 */
:global(.tooltip) {
  padding: 0.5rem 0.7rem !important;

  border: 1px solid
    rgba(var(--v-theme-on-surface), 0.14) !important;
  border-radius: 10px !important;

  color: rgb(var(--v-theme-on-surface)) !important;
  background-color: rgb(var(--v-theme-surface)) !important;

  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.18) !important;

  font-size: 0.82rem !important;
  font-weight: 600 !important;
  line-height: 1.3 !important;
}


/* Optional visuals while debugging (remove later)
.menu-strip { background: rgba(0, 128, 255, 0.06); }
.content-col { background: rgba(255, 128, 0, 0.06); }
*/


/* Hide the slim strip on small screens if you prefer */

/*
 * Die Bedingung muss der in `NavBar.vue` entsprechen.
 *
 * Dort wird die Navigation bei schmalem **oder niedrigem** Schirm zur
 * waagerechten Leiste. Bleibt diese Abfrage bei `max-width` allein, steht auf
 * 1366x768 eine Leiste mit `width: 100%` in einer 72 px schmalen Spalte — die
 * Inhaltsspalte legt sich darüber, und keiner der Knöpfe ist mehr
 * anklickbar. Im Browser nachgemessen: `elementFromPoint` lieferte dort für
 * jeden Eintrag `DIV.v-col-md-6` statt des Symbols.
 */
@media (max-width: 1300px), (max-height: 859px) {
  .row-no-wrap { flex-wrap: wrap !important; }
  .menu-strip {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    margin-bottom: 8px;
  }
  .content-col {
    flex: 1 1 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
  .page-gutters {
    padding-inline: 4px;
  }

}

</style>
